import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * LIGHTBOX
 *
 * The photo viewer: a sheet that rises from the bottom of the screen with the
 * picture on a dark stage and a panel of activity beside it — under it on a
 * phone, to the right of it from `md` up.
 *
 * The stage stays dark in either theme on purpose. The point of a lightbox is
 * to get the app out of the way and let the image carry the frame; flipping
 * the stage with the theme would wash the picture out in light mode. That is
 * the one colour here that is deliberately not a token, for the same reason
 * any lightbox is black.
 */
export const lightboxSheetRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'fixed inset-x-0 bottom-0 z-[70] flex flex-col overflow-hidden md:flex-row',
    'border-t border-border bg-background shadow-e3',
    'duration-slow data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
  ],
  {
    variants: {
      /**
       * `partial` leaves the page showing above it, as a sheet does. `full`
       * is for a screen that is already full-bleed, where a partial sheet
       * reads as cut off.
       */
      height: {
        partial: 'h-[92vh] rounded-t-2xl md:h-[80vh]',
        full: 'h-full',
      },
    },
    defaultVariants: { height: 'partial' },
  }
);

/** The drag affordance across the top on a phone. Nothing from `md` up. */
export const lightboxHandleRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex shrink-0 justify-center pb-1 pt-2 md:hidden',
]);

export const lightboxHandleBarRecipe = cva([
  uiMarker,
  'h-1 w-10 rounded-full bg-muted-foreground/40',
]);

/** Where the picture sits. */
export const lightboxStageRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'relative flex items-center justify-center p-3 md:p-4',
    // design-tokens-ignore -- a lightbox stage is dark in both themes; see the note above.
    'bg-[hsl(222_47%_11%)]',
  ],
  {
    variants: {
      /**
       * `beside` shares the sheet with a panel: the top on a phone, the left
       * from `md` up. `alone` is the whole sheet, for a viewer with no panel.
       */
      layout: {
        beside: 'h-[38vh] shrink-0 md:h-auto md:flex-1',
        alone: 'min-h-0 flex-1',
      },
    },
    defaultVariants: { layout: 'beside' },
  }
);
export type LightboxStageRecipeProps = VariantProps<typeof lightboxStageRecipe>;

/** The picture: contained, never cropped, on its own shadow. */
export const lightboxImageRecipe = cva([
  uiMarker,
  'max-h-full max-w-full rounded object-contain shadow-e3',
]);

/** The activity column beside (or under) the stage. */
export const lightboxPanelRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex min-h-0 flex-1 flex-col border-t border-border bg-background',
  'md:w-[400px] md:flex-none md:border-l md:border-t-0',
]);

/**
 * The scrim behind the sheet. Above the overlay recipe's `z-50` so it covers
 * whichever dialog the viewer opened from, and the same dark in both themes
 * as the stage: a scrim that flipped to `foreground/40` in dark mode would
 * wash the picture out.
 */
export const lightboxBackdropRecipe = cva([
  uiMarker,
  'fixed inset-0 z-[60] backdrop-blur-[2px]',
  // design-tokens-ignore -- the viewer's scrim stays dark in both themes; see the note above.
  'bg-[hsl(222_47%_11%/0.4)]',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
]);

export type LightboxSheetRecipeProps = VariantProps<typeof lightboxSheetRecipe>;
